// src/components/ManajemenSiswa.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { api } from '../services/api';

export default function ManajemenSiswa({ onBack }) {
  const [siswaList, setSiswaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [form, setForm] = useState({ nama: '', nisn: '', kelas: '', jenisKelamin: 'Laki-laki', status: 'Aktif' });
  const [saving, setSaving] = useState(false);

  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const [search, setSearch] = useState('');
  const [filterKelas, setFilterKelas] = useState('Semua Kelas');
  const [filterJk, setFilterJk] = useState('Semua');
  const [filterStatus, setFilterStatus] = useState('Aktif');

  const [selected, setSelected] = useState([]);
  const [kelasTujuan, setKelasTujuan] = useState('');

  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const fileInputRef = useRef(null);

  const loadSiswa = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.getSiswa('Semua Kelas');
      setSiswaList(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSiswa(); }, [loadSiswa]);

  function showInfo(msg) {
    setInfo(msg);
    setTimeout(() => setInfo(''), 3000);
  }

  async function handleTambah(e) {
    e.preventDefault();
    if (!form.nama || !form.nisn || !form.kelas) {
      setError('Nama, NISN, dan Kelas wajib diisi.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.addSiswa(form);
      setForm({ nama: '', nisn: '', kelas: '', jenisKelamin: 'Laki-laki', status: 'Aktif' });
      showInfo('Siswa berhasil ditambahkan.');
      loadSiswa();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function startEdit(s) {
    setEditId(s.ID);
    setEditForm({ nama: s.Nama, nisn: s.NISN, kelas: s.Kelas, jenisKelamin: s.JenisKelamin, status: s.Status });
  }

  async function saveEdit() {
    try {
      await api.updateSiswa({ id: editId, ...editForm });
      setEditId(null);
      showInfo('Data siswa diperbarui.');
      loadSiswa();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleNonAktifkan(id) {
    if (!window.confirm('Non-aktifkan siswa ini?')) return;
    try {
      await api.nonAktifkanSiswa(id);
      showInfo('Siswa dinonaktifkan.');
      loadSiswa();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleHapusSatu(id) {
    if (!window.confirm('Hapus PERMANEN siswa ini? Data tidak bisa dikembalikan.')) return;
    try {
      await api.hapusSiswa([id]);
      showInfo('Siswa dihapus.');
      loadSiswa();
    } catch (err) {
      setError(err.message);
    }
  }

  function toggleSelect(id) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function handleBulkHapus() {
    if (selected.length === 0) return;
    if (!window.confirm(`Hapus PERMANEN ${selected.length} siswa terpilih?`)) return;
    try {
      await api.hapusSiswa(selected);
      setSelected([]);
      showInfo('Siswa terpilih dihapus.');
      loadSiswa();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleBulkPindah() {
    if (selected.length === 0 || !kelasTujuan) return;
    try {
      await api.pindahKelas(selected, kelasTujuan);
      setSelected([]);
      setKelasTujuan('');
      showInfo('Kelas siswa terpilih diperbarui.');
      loadSiswa();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDownloadTemplate() {
    setError('');
    try {
      const res = await api.getTemplateUrl();
      window.open(res.url, '_blank');
    } catch (err) {
      setError(err.message);
    }
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportSummary(null);
    setError('');

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames.includes('Template_Import_Siswa')
          ? 'Template_Import_Siswa'
          : workbook.SheetNames[0];
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

        const siswaListBaru = rows.map((r) => ({
          nama: r.Nama || r.nama || '',
          nisn: String(r.NISN || r.nisn || '').trim(),
          kelas: r.Kelas || r.kelas || '',
          jenisKelamin: r['Jenis Kelamin'] || r.JenisKelamin || r.jenisKelamin || 'Laki-laki',
        }));

        const result = await api.importSiswa(siswaListBaru);
        setImportSummary(result);
        showInfo(`${result.ditambahkan} siswa berhasil diimpor.`);
        loadSiswa();
      } catch (err) {
        setError('Gagal membaca file: ' + err.message);
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsArrayBuffer(file);
  }

  const filtered = siswaList.filter((s) => {
    const matchSearch = !search || `${s.Nama} ${s.NISN} ${s.Kelas}`.toLowerCase().includes(search.toLowerCase());
    const matchKelas = filterKelas === 'Semua Kelas' || s.Kelas === filterKelas;
    const matchJk = filterJk === 'Semua' || s.JenisKelamin === filterJk;
    const matchStatus = filterStatus === 'Semua' || s.Status === filterStatus;
    return matchSearch && matchKelas && matchJk && matchStatus;
  });

  // Daftar kelas dibuat otomatis dari data siswa yang ada (manual maupun import).
  const kelasList = Array.from(new Set(siswaList.map((s) => s.Kelas).filter(Boolean))).sort();

  const inputCls =
    'w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const labelCls = 'block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* HEADER */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="border-l-4 border-blue-600 pl-3">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Manajemen Siswa</h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              {siswaList.length} total siswa &middot; {filtered.length} sesuai filter
            </p>
          </div>
          <button
            onClick={onBack}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium px-4 py-2 rounded-lg transition"
          >
            &larr; Kembali ke Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
        )}
        {info && (
          <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl px-4 py-3">{info}</div>
        )}

        {/* TAMBAH SISWA */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="bg-blue-600 px-5 sm:px-6 py-3.5">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <span aria-hidden="true">&#10133;</span> Tambah Siswa Baru
            </h2>
          </div>
          <div className="p-5 sm:p-6">
          <form onSubmit={handleTambah}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className={labelCls}>Nama Siswa</label>
                <input
                  value={form.nama}
                  onChange={(e) => setForm({ ...form, nama: e.target.value })}
                  className={inputCls}
                  placeholder="Nama Lengkap"
                />
              </div>
              <div>
                <label className={labelCls}>NISN</label>
                <input
                  value={form.nisn}
                  onChange={(e) => setForm({ ...form, nisn: e.target.value })}
                  className={inputCls}
                  placeholder="NISN"
                />
              </div>
              <div>
                <label className={labelCls}>Kelas</label>
                <input
                  value={form.kelas}
                  onChange={(e) => setForm({ ...form, kelas: e.target.value })}
                  className={inputCls}
                  placeholder="Contoh: XI A"
                />
              </div>
              <div>
                <label className={labelCls}>Jenis Kelamin</label>
                <select
                  value={form.jenisKelamin}
                  onChange={(e) => setForm({ ...form, jenisKelamin: e.target.value })}
                  className={inputCls}
                >
                  <option>Laki-laki</option>
                  <option>Perempuan</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className={inputCls}
                >
                  <option>Aktif</option>
                  <option>NonAktif</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium text-sm px-6 py-2.5 rounded-lg transition"
              >
                {saving ? 'Menyimpan...' : 'Tambah Siswa'}
              </button>
            </div>
          </form>
          </div>
        </section>

        {/* IMPOR EXCEL */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="bg-emerald-600 px-5 sm:px-6 py-3.5">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <span aria-hidden="true">&#128229;</span> Impor Siswa dari Excel
            </h2>
          </div>
          <div className="p-5 sm:p-6">
          <p className="text-xs text-slate-500 mb-4">
            Untuk menambah banyak siswa sekaligus. Kolom Jenis Kelamin di template sudah berupa dropdown.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              disabled={importing}
              className="flex-1 min-w-[220px] text-sm text-slate-600 bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-green-600 file:text-white file:text-sm file:font-medium"
            />
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition whitespace-nowrap"
            >
              Unduh Contoh Format
            </button>
          </div>
          {importing && <p className="text-sm text-slate-500 mt-3">Mengimpor data...</p>}
          {importSummary && (
            <div className="mt-3 bg-slate-50 rounded-xl p-4 text-sm border border-slate-100">
              <p className="font-medium text-slate-800 mb-1">
                Hasil impor: {importSummary.ditambahkan} siswa berhasil ditambahkan
                {importSummary.dilewati.length > 0 && `, ${importSummary.dilewati.length} dilewati`}.
              </p>
              {importSummary.dilewati.length > 0 && (
                <ul className="list-disc pl-5 text-slate-600 space-y-0.5">
                  {importSummary.dilewati.map((d, i) => (
                    <li key={i}>NISN {d.nisn} — {d.alasan}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          </div>
        </section>

        {/* FILTER */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="bg-violet-600 px-5 sm:px-6 py-3.5">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <span aria-hidden="true">&#128269;</span> Filter &amp; Cari
            </h2>
          </div>
          <div className="p-5 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className={labelCls}>Cari Siswa</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={inputCls}
                placeholder="Nama, NISN, atau kelas..."
              />
            </div>
            <div>
              <label className={labelCls}>Kelas</label>
              <select value={filterKelas} onChange={(e) => setFilterKelas(e.target.value)} className={inputCls}>
                <option>Semua Kelas</option>
                {kelasList.map((k) => (
                  <option key={k}>{k}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Jenis Kelamin</label>
              <select value={filterJk} onChange={(e) => setFilterJk(e.target.value)} className={inputCls}>
                <option>Semua</option>
                <option>Laki-laki</option>
                <option>Perempuan</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={inputCls}>
                <option>Semua</option>
                <option>Aktif</option>
                <option>NonAktif</option>
              </select>
            </div>
          </div>
          </div>
        </section>

        {/* DAFTAR SISWA */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="bg-indigo-600 px-5 sm:px-6 py-3.5">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <span aria-hidden="true">&#128203;</span> Daftar Siswa
            </h2>
          </div>

          {/* header kolom — desktop saja */}
          <div className="hidden sm:flex items-center gap-3 px-6 pt-4 pb-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">
            <span className="w-5 shrink-0" />
            <span className="flex-1">Nama Siswa</span>
            <span className="w-20 shrink-0 text-center">Kelas</span>
            <span className="w-24 shrink-0 text-center">Gender</span>
            <span className="w-20 shrink-0 text-center">Status</span>
            <span className="w-[190px] shrink-0 text-right">Aksi</span>
          </div>
          <div className="border-t border-slate-100" />

          {loading ? (
            <p className="text-center text-slate-500 py-10">Memuat data siswa...</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-10">Belum ada data siswa.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((s) => (
                <div key={s.ID} className="px-4 sm:px-6 py-3">
                  {editId === s.ID ? (
                    <div className="bg-blue-50 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                      <input
                        value={editForm.nama}
                        onChange={(e) => setEditForm({ ...editForm, nama: e.target.value })}
                        className={inputCls}
                        placeholder="Nama"
                      />
                      <input
                        value={editForm.nisn}
                        onChange={(e) => setEditForm({ ...editForm, nisn: e.target.value })}
                        className={inputCls}
                        placeholder="NISN"
                      />
                      <input
                        value={editForm.kelas}
                        onChange={(e) => setEditForm({ ...editForm, kelas: e.target.value })}
                        className={inputCls}
                        placeholder="Kelas"
                      />
                      <select
                        value={editForm.jenisKelamin}
                        onChange={(e) => setEditForm({ ...editForm, jenisKelamin: e.target.value })}
                        className={inputCls}
                      >
                        <option>Laki-laki</option>
                        <option>Perempuan</option>
                      </select>
                      <div className="flex gap-2">
                        <button onClick={saveEdit} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-lg py-2 text-sm font-medium transition">
                          Simpan
                        </button>
                        <button onClick={() => setEditId(null)} className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg py-2 text-sm font-medium transition">
                          Batal
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* ===== VERSI HP: kartu terstruktur ===== */}
                      <div className="sm:hidden">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 min-w-0">
                            <input
                              type="checkbox"
                              checked={selected.includes(s.ID)}
                              onChange={() => toggleSelect(s.ID)}
                              className="w-4 h-4 mt-1 shrink-0 accent-blue-600"
                            />
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-800 text-sm truncate">{s.Nama}</p>
                              <p className="text-xs text-slate-400">NISN: {s.NISN}</p>
                            </div>
                          </div>
                          <span className="shrink-0 text-xs font-medium text-slate-600 bg-slate-100 rounded-full px-2.5 py-1">
                            {s.Kelas}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 mt-2.5 ml-6">
                          <span className="text-xs text-slate-500">{s.JenisKelamin}</span>
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-medium ${
                              s.Status === 'Aktif' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'
                            }`}
                          >
                            {s.Status}
                          </span>
                        </div>

                        <div className="flex gap-1.5 mt-3 ml-6">
                          <button
                            onClick={() => startEdit(s)}
                            className="text-xs font-medium px-2.5 py-1.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleNonAktifkan(s.ID)}
                            className="text-xs font-medium px-2.5 py-1.5 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 transition"
                          >
                            Non-Aktif
                          </button>
                          <button
                            onClick={() => handleHapusSatu(s.ID)}
                            className="text-xs font-medium px-2.5 py-1.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100 transition"
                          >
                            Hapus
                          </button>
                        </div>
                      </div>

                      {/* ===== VERSI TABLET/DESKTOP: 1 baris rata kanan-kiri ===== */}
                      <div className="hidden sm:flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selected.includes(s.ID)}
                          onChange={() => toggleSelect(s.ID)}
                          className="w-5 shrink-0 accent-blue-600"
                        />
                        <div className="flex-1 min-w-[160px]">
                          <p className="font-semibold text-slate-800 text-sm">{s.Nama}</p>
                          <p className="text-xs text-slate-400">NISN: {s.NISN}</p>
                        </div>
                        <span className="w-20 shrink-0 text-center text-xs font-medium text-slate-600 bg-slate-100 rounded-full px-2 py-1">
                          {s.Kelas}
                        </span>
                        <span className="w-24 shrink-0 text-center text-xs text-slate-500">{s.JenisKelamin}</span>
                        <span
                          className={`w-20 shrink-0 text-center text-xs px-2 py-1 rounded-full font-medium ${
                            s.Status === 'Aktif' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'
                          }`}
                        >
                          {s.Status}
                        </span>
                        <div className="w-[190px] shrink-0 flex justify-end gap-1.5">
                          <button
                            onClick={() => startEdit(s)}
                            className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleNonAktifkan(s.ID)}
                            className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 transition"
                          >
                            Non-Aktif
                          </button>
                          <button
                            onClick={() => handleHapusSatu(s.ID)}
                            className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-700 hover:bg-red-100 transition"
                          >
                            Hapus
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* AKSI MASSAL */}
          <div className="bg-slate-50 border-t border-slate-100 px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
            <span className="text-sm text-slate-600 font-medium">{selected.length} siswa terpilih</span>
            <select
              value={kelasTujuan}
              onChange={(e) => setKelasTujuan(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
            >
              <option value="">Pindahkan ke kelas...</option>
              {kelasList.map((k) => (
                <option key={k}>{k}</option>
              ))}
            </select>
            <button
              onClick={handleBulkPindah}
              disabled={selected.length === 0 || !kelasTujuan}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
            >
              Pindahkan Kelas
            </button>
            <button
              onClick={handleBulkHapus}
              disabled={selected.length === 0}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded-lg transition ml-auto"
            >
              Hapus Terpilih
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
